import React from 'react';
import { motion } from 'framer-motion';
import { BellIcon, FolderOpenIcon, HomeIcon, LogOutIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { realtor } from '../data/portal';
import type { Brand, Page } from '../types/portal';
interface AppShellProps {
  brand: Brand;
  page: Page;
  onNavigate: (page: Page) => void;
  onSignOut: () => void;
  children: React.ReactNode;
}
const navItems: {
  id: Page;
  label: string;
  icon: typeof HomeIcon;
}[] = [{
  id: 'home',
  label: 'Home',
  icon: HomeIcon
}, {
  id: 'files',
  label: 'My Files',
  icon: FolderOpenIcon
}, {
  id: 'refer',
  label: 'Refer a Buyer',
  icon: UserPlusIcon
}, {
  id: 'team',
  label: 'My Team',
  icon: UsersIcon
}];
export function AppShell({
  brand,
  page,
  onNavigate,
  onSignOut,
  children
}: AppShellProps) {
  return <div className="flex min-h-screen w-full flex-col bg-slate-50" data-id="element-13" data-mp="1z7g029y15ity">
      <header className="sticky top-0 z-30 flex h-16 items-center border-b border-slate-200 bg-white" data-id="element-14" data-mp="23zhkij5cqfv2">
        <div className="flex w-60 shrink-0 items-center px-6" data-id="element-15" data-mp="1rcbwkk63o0w9">
          <BrandLogo brand={brand} data-id="element-16" data-mp="1raiqp73b9kpb" />
        </div>
        <div className="flex flex-1 items-center justify-between pl-10 pr-6" data-id="element-17" data-mp="1metmgqgcjfmo">
          <span className="text-sm font-medium text-slate-500" data-id="element-18" data-mp="2136oa4247sp3">Partner Portal</span>
          <div className="flex items-center gap-4" data-id="element-19" data-mp="1wbya6upubj5l">
            <button aria-label="Notifications, 2 new" className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-20" data-mp="1wadj5f2qiich">
              <BellIcon className="h-5 w-5" data-id="element-21" data-mp="2f3r5wwwanih0" />
              <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-accent ring-2 ring-white" data-id="element-22" data-mp="2f0ldb7oghygj" />
            </button>
            <span className="h-8 w-px bg-slate-200" aria-hidden="true" data-id="element-23" data-mp="koyfdca9phvx" />
            <div className="flex items-center gap-3" data-id="element-24" data-mp="1ack9gj3jrrqx">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-sm font-semibold text-white" data-id="element-25" data-mp="1advkn3zbo1s2">
                {realtor.initials}
              </span>
              <span className="text-sm leading-tight" data-id="element-26" data-mp="1jc7av4wa0xjz">
                <span className="block font-semibold text-slate-900" data-id="element-27" data-mp="1ocbtp1knq4dd">{realtor.name}</span>
                <span className="block text-slate-500" data-id="element-28" data-mp="1o9z2dxt0ehnn">{realtor.company}</span>
              </span>
            </div>
            <button onClick={onSignOut} aria-label="Sign out" className="ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-29" data-mp="1xd05dpmkvsn3">
              <LogOutIcon className="h-4 w-4" data-id="element-30" data-mp="2h0r6rik8acm2" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1" data-id="element-31" data-mp="5e6vitjsinu1">
        <aside className="sticky top-16 h-[calc(100vh-4rem)] w-60 shrink-0 border-r border-slate-200 bg-white px-3 py-6" data-id="element-32" data-mp="5f8j1xhzt2sn">
          <nav aria-label="Main" data-id="element-33" data-mp="gnj7z6nkl4q">
            <ul className="space-y-1" data-id="element-34" data-mp="3rkc5bamg043t">
              {navItems.map(({
              id,
              label,
              icon: Icon
            }) => {
              const active = page === id || id === 'files' && page === 'loan';
              return <li key={id} data-id="element-35" data-mp="o4essv0t6k2b">
                    <button onClick={() => onNavigate(id)} aria-current={active ? 'page' : undefined} className={`relative isolate flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${active ? 'font-semibold text-brand' : 'font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-36" data-mp="o3lwyp3e1eyy">
                      {active && <motion.span layoutId="nav-active" aria-hidden="true" className="absolute inset-0 -z-10 rounded-lg bg-brand-soft" transition={{
                    duration: 0.25,
                    ease: [0.23, 1, 0.32, 1]
                  }} data-id="element-37" data-mp="rlrevqnrsd0m" />}
                      <Icon className={`h-[18px] w-[18px] transition-colors duration-200 ${active ? 'text-brand' : 'text-slate-400'}`} data-id="element-38" data-mp="1abhj14dfab57" />
                      {label}
                    </button>
                  </li>;
            })}
            </ul>
          </nav>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col" data-id="element-39" data-mp="1ob8tww8n7bez">
          <main className="flex-1 px-10 py-8" data-id="element-40" data-mp="1o9pd94j2qyco">{children}</main>
          <footer className="border-t border-slate-200 px-10 py-5 text-xs text-slate-500" data-id="element-41" data-mp="1t2ijjwu6yeqv">{brand.legal}</footer>
        </div>
      </div>
    </div>;
}